/**
 * Core primitives — ported from the design system's compiled bundle and the console kit.
 *
 * Every visual value here is a token. There are no colour literals: the grep gate fails the build
 * on one, and a theme test asserts the same for the stylesheet. That is what makes the dark scheme
 * work without a second implementation.
 *
 * Two rules from the design system that are easy to lose in a port, so they are enforced in code:
 *   - Disabled is the `sunk` fill with `ink3` content — NEVER opacity alone. Opacity on a disabled
 *     control dims the label below AA and makes it unreadable rather than merely unavailable.
 *   - Selected always adds a tick. Colour never carries meaning alone, anywhere in this system.
 */
import type { CSSProperties, ReactNode, MouseEventHandler } from 'react';
import { ICONS, type IconName } from './icons';
import { useConsoleLabels } from '../lib/api/hooks/enumLabels';

/* ────────────────────────────  Icon  ──────────────────────────── */

export interface IconProps {
  name: IconName;
  size?: number;
  color?: string;
  title?: string;
  style?: CSSProperties;
}

/**
 * Icons inherit `currentColor` and carry no colour of their own. A decorative icon is
 * aria-hidden; one with a `title` is labelled, because meaning never travels alone.
 */
export function Icon({ name, size = 16, color, title, style }: IconProps) {
  const Glyph = ICONS[name];
  return (
    <Glyph
      size={size}
      color={color ?? 'currentColor'}
      strokeWidth={2}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      role={title ? 'img' : undefined}
      style={{ flex: '0 0 auto', ...style }}
    />
  );
}

/* ────────────────────────────  Button  ──────────────────────────── */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive';
export type ButtonSize = 'lg' | 'md' | 'sm';

export interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children?: ReactNode;
  icon?: IconName;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  type?: 'button' | 'submit';
  style?: CSSProperties;
  /** The accessible name, when the visible label is short because the row already says whose. */
  ariaLabel?: string;
}

const BUTTON_HEIGHT: Record<ButtonSize, string> = {
  lg: '48px',
  md: '40px',
  sm: '32px',
};

function buttonSkin(variant: ButtonVariant, disabled: boolean): CSSProperties {
  if (disabled) {
    // sunk fill + ink3 content, no elevation — never opacity
    return { background: 'var(--c-sunk)', color: 'var(--c-ink3)', borderColor: 'var(--c-border)' };
  }
  switch (variant) {
    // The deep fills, not the flat ones: the console's labels are 13–14 px, and cream on flat
    // saffron (3.2:1) or flat error (4.1:1) is below AA there. Flat saffron under a label is for
    // ≥18 px semibold CTAs only (brand doc 03 §10, doc 08 §4); the console has none.
    case 'primary':
      return { background: 'var(--c-saffron-deep)', color: 'var(--c-ink-inverse)', borderColor: 'var(--c-saffron-deep)' };
    case 'secondary':
      return { background: 'var(--c-surface2)', color: 'var(--c-ink)', borderColor: 'var(--c-border-strong)' };
    case 'ghost':
      return { background: 'transparent', color: 'var(--c-ink2)', borderColor: 'transparent' };
    case 'destructive':
      return { background: 'var(--c-error-text)', color: 'var(--c-ink-inverse)', borderColor: 'var(--c-error-text)' };
  }
}

export function Button({
  variant = 'primary',
  size = 'md',
  children,
  icon,
  loading = false,
  disabled = false,
  fullWidth = false,
  onClick,
  type = 'button',
  style,
  ariaLabel,
}: ButtonProps) {
  const isDisabled = disabled || loading;
  return (
    <button
      type={type}
      disabled={isDisabled}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-busy={loading || undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--sp-sm)',
        height: BUTTON_HEIGHT[size],
        padding: size === 'sm' ? '0 var(--sp-md)' : '0 var(--sp-lg)',
        width: fullWidth ? '100%' : undefined,
        border: '1px solid',
        borderRadius: 'var(--r-tile)',
        fontFamily: 'var(--font-display)',
        fontSize: size === 'sm' ? 'var(--fs-admin-cell)' : 'var(--fs-admin-ui)',
        fontWeight: 'var(--fw-semibold)',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        whiteSpace: 'nowrap',
        ...buttonSkin(variant, isDisabled),
        ...style,
      }}
    >
      {loading ? (
        // The label is swapped for a spinner with the width locked, so the control does not jump.
        <span data-testid="button-spinner" style={{ display: 'inline-flex' }}>
          <Icon name="refresh-cw" size={14} />
        </span>
      ) : (
        icon && <Icon name={icon} size={size === 'sm' ? 14 : 16} />
      )}
      {children}
    </button>
  );
}

/* ────────────────────────────  Card  ──────────────────────────── */

export type CardVariant = 'flat' | 'elevated' | 'hero' | 'darkHero';

export interface CardProps {
  variant?: CardVariant;
  padding?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
  children?: ReactNode;
  style?: CSSProperties;
}

const CARD_SKIN: Record<CardVariant, CSSProperties> = {
  // Cream-on-cream needs edges: the hairline defines the container, not a shadow.
  flat: { background: 'var(--c-surface)', boxShadow: 'none', borderRadius: 'var(--r-card)' },
  elevated: { background: 'var(--c-surface)', boxShadow: 'var(--sh-sm)', borderRadius: 'var(--r-card)' },
  hero: { background: 'var(--c-surface)', boxShadow: 'var(--sh-md)', borderRadius: 'var(--r-card-lg)' },
  darkHero: {
    background: 'var(--c-ink)',
    color: 'var(--c-ink-inverse)',
    boxShadow: 'var(--sh-md)',
    borderRadius: 'var(--r-card-lg)',
  },
};

export function Card({ variant = 'flat', padding = 'var(--sp-lg)', onClick, children, style }: CardProps) {
  return (
    <div
      onClick={onClick}
      style={{
        border: '1px solid var(--c-border)',
        padding,
        cursor: onClick ? 'pointer' : undefined,
        ...CARD_SKIN[variant],
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* ────────────────────────────  Chip  ──────────────────────────── */

export type ChipVariant = 'display' | 'filter' | 'trust';

export interface ChipProps {
  label: string;
  icon?: IconName;
  variant?: ChipVariant;
  selected?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  style?: CSSProperties;
}

export function Chip({ label, icon, variant = 'display', selected = false, onClick, disabled = false, style }: ChipProps) {
  const interactive = variant === 'filter' && !disabled;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-pressed={variant === 'filter' ? selected : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--sp-xs)',
        height: 28,
        padding: '0 var(--sp-md)',
        borderRadius: 'var(--r-chip)',
        border: '1px solid',
        borderColor: selected ? 'var(--c-saffron)' : 'var(--c-border-strong)',
        background: disabled
          ? 'var(--c-sunk)'
          : selected
            ? 'var(--c-saffron-50)'
            : variant === 'trust'
              ? 'var(--c-success-bg)'
              : 'var(--c-surface2)',
        color: disabled
          ? 'var(--c-ink3)'
          : selected
            ? 'var(--c-saffron-text)'
            : variant === 'trust'
              ? 'var(--c-success-text)'
              : 'var(--c-ink2)',
        fontFamily: 'var(--font-body)',
        fontSize: 'var(--fs-admin-cell)',
        cursor: interactive ? 'pointer' : 'default',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {/* Selected always adds a tick — colour never carries meaning alone. */}
      {selected && <Icon name="check" size={13} />}
      {icon && !selected && <Icon name={icon} size={13} />}
      {label}
    </button>
  );
}

/* ────────────────────────────  StatusChip  ──────────────────────────── */

export type Tone = 'neutral' | 'success' | 'warn' | 'error' | 'info';

type ChipWords =
  /** Words the caller already has. */
  | { label: string; value?: never; enumName?: never }
  /**
   * A state code, worded from the admin-editable `enum_label` rows (05-admin/03 §6.11). With no
   * row for it the raw code shows in mono, so a missing label is visible rather than invisible.
   */
  | { value: string; enumName: string; label?: never };

export type StatusChipProps = ChipWords & {
  tone?: Tone;
  icon?: IconName;
  style?: CSSProperties;
};

/**
 * Every status is a triad: the accent for borders, the `*Text` twin for body-size text, the `*Bg`
 * tint for the fill. That is how the deck's exact hexes survive WCAG AA at 13px.
 */
const TONE: Record<Tone, { bg: string; fg: string; border: string }> = {
  neutral: { bg: 'var(--c-sunk)', fg: 'var(--c-ink2)', border: 'var(--c-border-strong)' },
  success: { bg: 'var(--c-success-bg)', fg: 'var(--c-success-text)', border: 'var(--c-success)' },
  warn: { bg: 'var(--c-warn-bg)', fg: 'var(--c-warn-text)', border: 'var(--c-warn)' },
  error: { bg: 'var(--c-error-bg)', fg: 'var(--c-error-text)', border: 'var(--c-error)' },
  info: { bg: 'var(--c-info-bg)', fg: 'var(--c-info-text)', border: 'var(--c-info)' },
};

/* Only a chip given a code reads the labels; the hundreds that pass words never subscribe. */
function EnumWords({ value, enumName }: { value: string; enumName: string }) {
  const labels = useConsoleLabels();
  const row = labels.data?.items.find((l) => l.enum_name === enumName && l.value === value);
  return row ? <>{row.label_en}</> : <span className="mono-num">{value}</span>;
}

export function StatusChip({ tone = 'neutral', label, value, enumName, icon, style }: StatusChipProps) {
  const t = TONE[tone];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--sp-xs)',
        padding: '2px var(--sp-sm)',
        borderRadius: 'var(--r-chip)',
        background: t.bg,
        color: t.fg,
        border: `1px solid ${t.border}`,
        fontFamily: 'var(--font-body)',
        fontSize: 'var(--fs-admin-colhead)',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {icon && <Icon name={icon} size={12} />}
      {value === undefined ? label : <EnumWords value={value} enumName={enumName!} />}
    </span>
  );
}

/* ────────────────────────────  Badge  ──────────────────────────── */

/**
 * The six named verification badges (R41). There is deliberately no generic "Verified" — a bare
 * tick claims something nobody checked, which is precisely the trust shortcut this product is
 * built to avoid. Adding a seventh badge is a trust claim, not a UI change: it needs ratifying.
 *
 * The glyphs are Lucide substitutes; the hand-drawn set (`id-card`, `bank-check`, `scroll-check`,
 * `medal`, `shikhara-check`, `shield-check`) is undrawn upstream. Swapping them later does not
 * change this API.
 */
export const BADGE_TYPES = {
  identity_verified: { label: 'Identity verified', icon: 'id-card' },
  bank_verified: { label: 'Bank verified', icon: 'banknote' },
  training_reference_verified: { label: 'References verified', icon: 'scroll-text' },
  platform_quality_checked: { label: 'Quality checked', icon: 'medal' },
  temple_authorized: { label: 'Temple authorized', icon: 'landmark' },
  background_verified: { label: 'Background verified', icon: 'shield-check' },
} as const satisfies Record<string, { label: string; icon: IconName }>;

export type BadgeType = keyof typeof BADGE_TYPES;
export type BadgeState = 'active' | 'expired' | 'revoked' | 'suspended' | 'not issued';

export interface BadgeProps {
  type: BadgeType;
  state?: BadgeState;
  compact?: boolean;
  style?: CSSProperties;
}

export function Badge({ type, state = 'active', compact = false, style }: BadgeProps) {
  const { label, icon } = BADGE_TYPES[type];
  const issued = state === 'active';
  const dead = state === 'expired' || state === 'revoked' || state === 'suspended';
  return (
    <span
      title={compact ? `${label} — ${state}` : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--sp-xs)',
        padding: compact ? '2px var(--sp-xs)' : '2px var(--sp-sm)',
        borderRadius: 'var(--r-chip)',
        border: `1px solid ${issued ? 'var(--c-success)' : 'var(--c-border-strong)'}`,
        background: issued ? 'var(--c-success-bg)' : 'var(--c-sunk)',
        color: issued ? 'var(--c-success-text)' : 'var(--c-ink3)',
        fontFamily: 'var(--font-body)',
        fontSize: 'var(--fs-admin-colhead)',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      <Icon name={icon} size={12} />
      {/* A dead badge reads dead (doc 05-admin/03 §6.10): struck through, its state named. */}
      {!compact && <span style={{ textDecoration: dead ? 'line-through' : undefined }}>{label}</span>}
      {!compact && !issued && <span style={{ fontStyle: 'italic' }}>· {state}</span>}
    </span>
  );
}

/* ────────────────────────────  Avatar  ──────────────────────────── */

export interface AvatarProps {
  name: string;
  src?: string;
  size?: number;
  kind?: 'devotee' | 'pandit' | 'admin';
  style?: CSSProperties;
}

/** Initials, never a generated cartoon. No photography exists for these people. */
export function Avatar({ name, src, size = 32, kind = 'admin', style }: AvatarProps) {
  const initials = name
    .replace(/^Pandit\s+/i, '')
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();
  return (
    <span
      aria-label={name}
      role="img"
      style={{
        display: 'inline-grid',
        placeItems: 'center',
        flex: '0 0 auto',
        width: size,
        height: size,
        borderRadius: 'var(--r-pill)',
        background: kind === 'pandit' ? 'var(--c-saffron-50)' : 'var(--c-sunk)',
        color: kind === 'pandit' ? 'var(--c-saffron-text)' : 'var(--c-ink2)',
        border: '1px solid var(--c-border-strong)',
        fontFamily: 'var(--font-display)',
        fontSize: Math.max(10, Math.round(size * 0.36)),
        fontWeight: 'var(--fw-semibold)',
        overflow: 'hidden',
        ...style,
      }}
    >
      {src ? <img src={src} alt="" width={size} height={size} style={{ objectFit: 'cover' }} /> : initials}
    </span>
  );
}

/* ────────────────────────────  Rating  ──────────────────────────── */

export interface RatingProps {
  value: number;
  count?: number;
  size?: 'inline' | 'profile';
  style?: CSSProperties;
}

/** Gold is the rating star. It is decorative — it never renders text and never signals state. */
export function Rating({ value, count, size = 'inline', style }: RatingProps) {
  const px = size === 'profile' ? 16 : 13;
  return (
    <span
      aria-label={`${value} out of 5`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-xs)', ...style }}
    >
      <Icon name="star" size={px} style={{ color: 'var(--c-rating-star)' }} />
      <span className="tabular" style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink)' }}>
        {value.toFixed(1)}
      </span>
      {count !== undefined && (
        <span className="tabular" style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
          ({count.toLocaleString('en-IN')})
        </span>
      )}
    </span>
  );
}

/* ────────────────────────────  Skeleton  ──────────────────────────── */

export interface SkeletonProps {
  width?: number | string;
  height?: number | string;
  radius?: string;
  style?: CSSProperties;
}

/** Content-shaped, never a spinner over stale content. */
export function Skeleton({ width = '100%', height = 12, radius = 'var(--r-tile)', style }: SkeletonProps) {
  return (
    <span
      data-testid="skeleton"
      aria-hidden
      style={{ display: 'block', width, height, borderRadius: radius, background: 'var(--c-sunk)', ...style }}
    />
  );
}

/* ────────────────────────────  Note  ──────────────────────────── */

/**
 * A sentence or two of guidance under a heading: sentence case, ink2. The kicker is for labels a
 * few words long; a paragraph set in tracked capitals is the hardest text on the screen to read.
 */
export function Note({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div style={{ fontSize: 'var(--fs-admin-cell)', lineHeight: 1.5, color: 'var(--c-ink2)', ...style }}>
      {children}
    </div>
  );
}

/* ────────────────────────────  Kicker  ──────────────────────────── */

/** Uppercase, +8% tracking, ink3 — the one sanctioned all-caps device in the console. */
export function Kicker({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <span className="kicker" style={style}>
      {children}
    </span>
  );
}
